// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
// COP — generic panel-mount host (Seam B, AOA-ATAK-INTEROP §4 / §3).
//
// The READ-BACK half of the C3 panel extension point. The host records every
// `ctx.registerPanel(spec)` into the registry; `CapabilityHost.panels()` enumerates
// them across capabilities; THIS component renders each one — with NO per-capability
// branching. For each PanelSpec it owns a location-aware dock container <div> and, in an
// effect, calls `spec.mount(containerEl)`, running the returned cleanup on unmount /
// spec-change. The capability owns what renders inside (its own React tree / DOM); the
// host owns ONLY the dock chrome + lifecycle (the three-planes split: engine / design /
// plugin API — App.tsx never reaches a capability's internals).
//
// This is what lets a THIRD-PARTY add-in put a panel on screen through the PUBLIC C3 API
// alone (the P3 encrypted-chat add-in depends on it). A PanelSpec with NO `mount` (e.g.
// the copilot, whose render is still the App.tsx side-channel pending migration) is
// SKIPPED here — so PanelHost can run alongside the legacy copilot wiring without
// double-rendering it.
//
// React lives HERE (out of the imperative render loop). Themed via design-token CSS vars
// (CONTRACT:C4-DESIGN-TOKENS.1.0) — no raw hex outside the host-owned dock chrome.

import { useEffect, useRef, type CSSProperties } from "react";
import type { PanelContribution } from "../../../../packages/capability-host/src/index.ts";
import { usePanelLayer } from "./panelStack.ts";

/** Dock geometry per preferred `location` (AOA §8 Q9: location-only today — size/
 *  dismissability are NOT yet in PanelSpec, so the host picks a sane default per
 *  side; the Z-ORDER now comes live from the shared panelStack focus band — the
 *  last-opened/clicked surface wins the front, so a dock can never pop under the
 *  slideout nor vice versa. When the contract grows dock richness, derive these
 *  from the spec). The copilot's bespoke bottom-center float stays hard-coded in
 *  App.tsx for now. */
const DOCK_BASE: CSSProperties = {
  position: "absolute",
  background: "var(--color-surface-overlay, rgba(9,14,20,.94))",
  border: "1px solid var(--ui-hairline, rgba(31,182,182,.3))",
  borderRadius: "var(--radius-panel, 12px)",
  boxShadow: "0 24px 70px rgba(0,0,0,.55)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const DOCK_BY_LOCATION: Record<"left" | "right" | "bottom", CSSProperties> = {
  left: { ...DOCK_BASE, left: 16, top: 64, bottom: 88, width: 360 },
  right: { ...DOCK_BASE, right: 16, top: 64, bottom: 88, width: 360 },
  bottom: { ...DOCK_BASE, left: "50%", transform: "translateX(-50%)", bottom: 72, width: 620, maxWidth: "calc(100% - 420px)", maxHeight: "60%" },
};

function dockStyle(location: "left" | "right" | "bottom" | undefined): CSSProperties {
  return DOCK_BY_LOCATION[location ?? "right"];
}

/** Run a PanelSpec's mount into a host-owned container and return a SAFE teardown.
 *  PURE of React (so it is unit-testable without a DOM renderer): calls `mount(el)` with
 *  the container, captures the optional cleanup, and isolates a throwing mount (a bad
 *  add-in must not take down the shell — the crash-isolation spirit). The returned
 *  teardown is idempotent-safe and isolates a throwing cleanup. A spec with no `mount`
 *  (or no element) is a no-op (returns an inert teardown). */
export function runPanelMount(
  el: unknown,
  spec: PanelContribution["spec"],
): () => void {
  const mount = spec.mount;
  if (el == null || typeof mount !== "function") return () => {};
  let cleanup: void | (() => void);
  try {
    // The capability renders its OWN content into the host-owned container. We pass the
    // raw element (the contract types `container` as `unknown` — the capability narrows).
    cleanup = mount(el);
  } catch {
    // A throwing mount must not take down the shell; leave the dock empty.
    cleanup = undefined;
  }
  return () => {
    if (typeof cleanup === "function") {
      try {
        cleanup();
      } catch {
        // best-effort teardown — a throwing cleanup must not block React unmount.
      }
    }
  };
}

/** A single mounted panel: a host-owned dock container the capability mounts into. The
 *  mount/cleanup is keyed on the spec's `mount` identity so a re-registered renderer
 *  re-mounts and the old cleanup runs first (no orphan DOM). */
function MountedPanel({ panel }: { panel: PanelContribution }): JSX.Element {
  const { spec } = panel;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // FOCUS/Z-ORDER: each dock joins the shared panel band (raise on mount = "opening
  // brings it to the front"; raise on pointerdown = "clicking an already-open panel
  // brings it to the front"; drop on unmount is the hook's cleanup). The focused
  // (front) dock picks up the teal focus hairline on its border.
  const layer = usePanelLayer(`dock:${panel.capabilityId}/${spec.id}`);
  const { raise } = layer;
  useEffect(() => {
    raise();
  }, [raise]);

  useEffect(() => {
    // Re-run if the capability supplies a NEW mount renderer (identity change); the
    // returned teardown runs the capability's cleanup on unmount / spec-change.
    return runPanelMount(containerRef.current, spec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.mount]);

  return (
    <div
      ref={containerRef}
      data-panel-id={spec.id}
      data-capability-id={panel.capabilityId}
      aria-label={spec.title}
      data-focused={layer.focused ? "" : undefined}
      onPointerDownCapture={raise}
      style={{
        ...dockStyle(spec.location),
        zIndex: layer.z,
        ...(layer.focused
          ? { borderColor: "color-mix(in srgb, var(--ui-accent, #1fb6b6) 55%, transparent)" }
          : null),
      }}
    />
  );
}

/** Render every host-enumerated panel that carries a `mount` renderer. Driven by
 *  `host.panels()` (re-read on the host's panel change-signal — see App.tsx). Keyed on
 *  `capabilityId/spec.id` so React preserves a panel's mounted DOM across re-renders and
 *  unmounts exactly the ones that left the enumeration (deactivated capability ⇒ cleanup). */
export function PanelHost({ panels }: { panels: PanelContribution[] }): JSX.Element {
  const mountable = panels.filter((p) => typeof p.spec.mount === "function");
  return (
    <>
      {mountable.map((p) => (
        <MountedPanel key={`${p.capabilityId}/${p.spec.id}`} panel={p} />
      ))}
    </>
  );
}
